// Modelo para las líneas de productos dentro de merchandise
export interface MerchandiseLine {
  id: number;
  merchandise_id: number;
  product_id: number;
  qty: number;
  pending_qty: number;
  received_qty: number;
  rejected_qty: number;
  product: {
    id: number;
    code: string;
    name: string;
    image_hash?: string;
  };
  receptions: any[];
}

// Domain model para Merchandise - Version API Completa
export interface Merchandise {
  id: number;
  ref: string;
  supplier: string;
  courier: string;
  tracking_number: string;
  estimate_delivery: string;
  status: number;
  team_id: number;
  team?: {
    id: number;
    name: string;
  };
  lines: MerchandiseLine[];
  created_at?: string;
  updated_at?: string;
}

// DTOs separados para requests
export interface CreateMerchandiseRequest {
  ref: string;
  supplier: string;
  courier: string;
  tracking_number: string;
  estimate_delivery: string;
  status: number;
  product_qty: number; // Para el formulario de creación
}

export interface UpdateMerchandiseRequest {
  ref: string;
  supplier: string;
  courier: string;
  tracking_number: string;
  estimate_delivery: string;
  status: number;
  product_qty: number; // Para el formulario de edición
}

// Filtros para búsqueda de merchandise
export interface MerchandiseFilters {
  searchTerm?: string;
  supplier?: string;
  courier?: string;
  status?: number;
  // Campos específicos procesados desde filterConditions
  supplier_ids?: string | string[];
  courier_ids?: string | string[];
  status_ids?: string | string[];
  // Campos para sorting
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Response paginada de la API (meta puede venir en camelCase o snake_case)
export interface MerchandiseApiResponse {
  data: Merchandise[];
  meta: {
    currentPage?: number;
    lastPage?: number;
    perPage?: number;
    total?: number;
    current_page?: number;
    last_page?: number;
    per_page?: number;
  };
}

// Para useInfiniteQuery
export interface MerchandisePage {
  merchandise: Merchandise[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// Enum para estados de mercancía
export enum MerchandiseStatus {
  CANCELED = 0,
  PENDING = 1,
  PARTIAL_RECEIVED = 2,
  RECEIVED = 3,
  CLOSED = 4,
}

// Mapeo de estados para mostrar en la UI (fallback - preferir usar i18n)
export const MERCHANDISE_STATUS_LABELS: Record<MerchandiseStatus, string> = {
  [MerchandiseStatus.CANCELED]: 'Cancelado',
  [MerchandiseStatus.PENDING]: 'Pendiente',
  [MerchandiseStatus.PARTIAL_RECEIVED]: 'Parcialmente recibido',
  [MerchandiseStatus.RECEIVED]: 'Recibido',
  [MerchandiseStatus.CLOSED]: 'Cerrado',
};

// Función helper para obtener status label con i18n
export const getMerchandiseStatusLabel = (
  status: MerchandiseStatus,
  t?: (key: string) => string
): string => {
  if (!t) return MERCHANDISE_STATUS_LABELS[status] || 'Desconocido';

  switch (status) {
    case MerchandiseStatus.PENDING:
      return t('status.pending');
    case MerchandiseStatus.PARTIAL_RECEIVED:
      return t('status.partial_received');
    case MerchandiseStatus.RECEIVED:
      return t('status.received');
    case MerchandiseStatus.CLOSED:
      return t('status.closed');
    default:
      return t('status.unknown');
  }
};

// Mapeo de colores para estados
export const MERCHANDISE_STATUS_COLORS: Record<MerchandiseStatus, string> = {
  [MerchandiseStatus.CANCELED]:
    'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  [MerchandiseStatus.PENDING]:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  [MerchandiseStatus.PARTIAL_RECEIVED]:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  [MerchandiseStatus.RECEIVED]:
    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  [MerchandiseStatus.CLOSED]:
    'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
};
